// Per-file lifecycle controls (Phase 3 FE) — file_purpose badge + approve/lock
// + public-link (QR) mint. Drops into a file row inside <AttachmentsPanel>.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PexIcon } from './icons/PexIcon'
import {
  approveFile,
  mintPublicToken,
  patchFilePurpose,
  setLock,
  type PublicTokenMint,
} from '../api/file-lifecycle'
import type { AttachmentRow } from '../api/attachments'
import { ShareLinkModal } from './ShareLinkModal'
import { useT } from '../i18n'
import { sharedUiI18n } from '../i18n/components/shared-ui'

const PURPOSE_VALUES = [
  'customer_upload',
  'design_source',
  'mockup',
  'approved_mockup',
  'production_final',
  'vector_output',
  'ai_composed',
] as const

export function FileLifecycleControls({
  file,
  invalidateKey,
}: {
  file: AttachmentRow
  invalidateKey: readonly unknown[]
}) {
  const t = useT(sharedUiI18n).fileLifecycle
  const qc = useQueryClient()
  const [mint, setMint] = useState<PublicTokenMint | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: invalidateKey })

  const approve = useMutation({
    mutationFn: () => approveFile(file.id),
    onSuccess: refresh,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.failed),
  })
  const lock = useMutation({
    mutationFn: (locked: boolean) => setLock(file.id, locked),
    onSuccess: refresh,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.failed),
  })
  const share = useMutation({
    mutationFn: () => mintPublicToken(file.id, { purpose: 'file_share' }),
    onSuccess: (m) => {
      setError(null)
      setMint(m)
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.failed),
  })
  const purpose = useMutation({
    mutationFn: (value: string) => patchFilePurpose(file.id, value),
    onSuccess: refresh,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.failed),
  })

  const busy = approve.isPending || lock.isPending || share.isPending

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
      <select
        value={file.file_purpose ?? ''}
        disabled={busy || purpose.isPending || file.is_locked}
        onChange={(e) => e.target.value && purpose.mutate(e.target.value)}
        title={t.purposeTitle}
        className="rounded border border-violet-200 bg-violet-50 px-1.5 py-1 text-[10px] font-medium uppercase tracking-wide text-violet-700 disabled:opacity-60"
      >
        <option value="">{t.purposePlaceholder}</option>
        {PURPOSE_VALUES.map((v) => (
          <option key={v} value={v}>
            {t.purposes[v]}
          </option>
        ))}
      </select>
      {file.is_locked ? (
        <button
          type="button"
          onClick={() => lock.mutate(false)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
          title={t.lockedTitle}
        >
          <PexIcon name="lock" className="h-[11px] w-[11px]" size="xs" tone="inherit" /> {t.locked}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={() => approve.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-emerald-200 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            title={t.approveTitle}
          >
            <PexIcon name="success" className="h-[11px] w-[11px]" size="xs" tone="inherit" /> {t.approve}
          </button>
          <button
            type="button"
            onClick={() => lock.mutate(true)}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            title={t.lockTitle}
          >
            <PexIcon name="unlock" className="h-[11px] w-[11px]" size="xs" tone="inherit" /> {t.lock}
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => share.mutate()}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
        title={t.publicLinkTitle}
      >
        {share.isPending ? (
          <PexIcon name="loading" className="h-[11px] w-[11px] animate-spin" size="xs" tone="inherit" />
        ) : (
          <PexIcon name="link" className="h-[11px] w-[11px]" size="xs" tone="inherit" />
        )}
        {t.publicLink}
      </button>
      {error && <span className="text-[11px] text-rose-600">{error}</span>}

      {mint && (
        <ShareLinkModal
          mint={mint}
          filename={file.original_filename}
          onClose={() => setMint(null)}
        />
      )}
    </div>
  )
}
